/**
 * SalesPerfDashboard — Iter Feb 2026 R47.
 *
 * Two tabs:
 *   1. Team Leaderboard — one row per rep with cross-channel totals.
 *   2. Per-Rep detail    — pick a rep → KPI cards + daily activity strip.
 *
 * Filter bar: date range (from/to) + channel select on leaderboard.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { PhoneCall, MessageCircle, Mail, Target, Trophy, Users, Filter, Search, TrendingUp, Loader2, Bell } from "lucide-react";

function fmtTalk(sec) {
  if (!sec) return "0m";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return h ? `${h}h ${m}m` : `${m}m`;
}

function isoDaysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

function useRange() {
  const [from, setFrom] = useState(isoDaysAgo(30));
  const [to, setTo] = useState(isoDaysAgo(0));
  return { from, to, setFrom, setTo };
}

// ── Team KPI strip ────────────────────────────────────────────────
function Kpi({ label, value, icon: Icon, testid, tone = "" }) {
  return (
    <div className="border p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className={`text-2xl font-bold tabular-nums mt-1 ${tone}`}>{value}</div>
    </div>
  );
}

function TeamSummary({ from, to }) {
  const { activeWorkspaceId } = useAuth();
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!activeWorkspaceId) return;
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get(`/workspaces/${activeWorkspaceId}/sales-perf/team-summary`, { params: { from_: from, to_: to } });
        if (!cancel) setData(data);
      } catch (e) { if (!cancel) toast.error(apiErrorMessage(e, "Team summary failed")); }
    })();
    return () => { cancel = true; };
  }, [activeWorkspaceId, from, to]);
  if (!data) return <div className="text-sm text-muted-foreground">Loading team totals…</div>;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2" data-testid="sales-perf-team-summary">
      <Kpi label="Active reps" value={data.reps_active} icon={Users} testid="kpi-reps-active" />
      <Kpi label="Calls" value={data.calls_total} icon={PhoneCall} testid="kpi-calls-total" />
      <Kpi label="Answered" value={data.calls_answered} icon={PhoneCall} testid="kpi-calls-answered" tone="text-emerald-600" />
      <Kpi label="Talk time" value={`${data.talk_time_hours}h`} icon={PhoneCall} testid="kpi-talk-time" />
      <Kpi label="WhatsApp sent" value={data.whatsapp_sent} icon={MessageCircle} testid="kpi-wa-sent" />
      <Kpi label="Emails sent" value={data.email_sent} icon={Mail} testid="kpi-email-sent" />
      <Kpi label="Prospects qualified" value={data.prospects_qualified} icon={Target} testid="kpi-qualified" />
      <Kpi label="Leads won" value={data.leads_won} icon={Trophy} testid="kpi-leads-won" tone="text-emerald-700" />
    </div>
  );
}

// ── Leaderboard ────────────────────────────────────────────────────
function Leaderboard({ from, to, onDrill }) {
  const { activeWorkspaceId } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [channel, setChannel] = useState("all");
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/sales-perf/leaderboard`, {
        params: { from_: from, to_: to, channel },
      });
      setRows(data.items || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Leaderboard failed")); }
    finally { setLoading(false); }
  }, [activeWorkspaceId, from, to, channel]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!q.trim()) return rows;
    const s = q.trim().toLowerCase();
    return rows.filter(r => (r.user_name || "").toLowerCase().includes(s) || (r.user_email || "").toLowerCase().includes(s));
  }, [rows, q]);

  return (
    <div className="space-y-3" data-testid="sales-perf-leaderboard">
      <div className="flex items-center gap-2 flex-wrap">
        <Filter className="w-3.5 h-3.5 text-muted-foreground" />
        <Select value={channel} onValueChange={setChannel}>
          <SelectTrigger className="h-8 rounded-none w-44 text-xs" data-testid="leaderboard-channel"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All channels (composite)</SelectItem>
            <SelectItem value="calls">Rank by Calls</SelectItem>
            <SelectItem value="whatsapp">Rank by WhatsApp</SelectItem>
            <SelectItem value="email">Rank by Email</SelectItem>
            <SelectItem value="prospecting">Rank by Prospecting</SelectItem>
            <SelectItem value="leads">Rank by Leads won</SelectItem>
          </SelectContent>
        </Select>
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter reps…" className="h-8 rounded-none pl-7 w-56 text-xs" data-testid="leaderboard-search" />
        </div>
        <div className="text-xs text-muted-foreground ml-auto">{filtered.length} rep{filtered.length === 1 ? "" : "s"}</div>
      </div>
      {loading ? <div className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
       : filtered.length === 0 ? <div className="border p-8 text-center text-sm text-muted-foreground">No activity in the selected range.</div> : (
        <div className="border">
          <table className="w-full text-xs">
            <thead className="bg-muted/30">
              <tr className="text-left">
                <th className="p-2 w-10">#</th>
                <th className="p-2">Rep</th>
                <th className="p-2 text-right">Score</th>
                <th className="p-2 text-right">Calls · Answ · Talk</th>
                <th className="p-2 text-right">WA sent</th>
                <th className="p-2 text-right">Email sent</th>
                <th className="p-2 text-right">Prospects Q</th>
                <th className="p-2 text-right">Leads Won / New</th>
                <th className="p-2 w-20"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, i) => (
                <tr key={r.user_id} className="border-t border-border hover:bg-muted/20" data-testid={`leaderboard-row-${r.user_id}`}>
                  <td className="p-2 tabular-nums text-muted-foreground">{i + 1}</td>
                  <td className="p-2">
                    <div className="font-medium">{r.user_name}</div>
                    <div className="text-[10px] text-muted-foreground truncate">{r.user_email || ""}</div>
                  </td>
                  <td className="p-2 text-right tabular-nums font-bold">{r.score}</td>
                  <td className="p-2 text-right tabular-nums">
                    {r.calls.total} · <span className="text-emerald-700 font-semibold">{r.calls.answered}</span> · {fmtTalk(r.calls.talk_time_sec)}
                  </td>
                  <td className="p-2 text-right tabular-nums">{r.whatsapp.messages_sent}</td>
                  <td className="p-2 text-right tabular-nums">{r.email.sent}</td>
                  <td className="p-2 text-right tabular-nums">{r.prospecting.qualified}</td>
                  <td className="p-2 text-right tabular-nums">
                    <span className="text-emerald-700 font-semibold">{r.leads.won}</span> / {r.leads.created}
                  </td>
                  <td className="p-2 text-right">
                    <Button variant="ghost" size="sm" className="rounded-none h-6 text-[10px]" onClick={() => onDrill(r.user_id)} data-testid={`leaderboard-drill-${r.user_id}`}>Drill</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Per-rep detail ────────────────────────────────────────────────
function PerRep({ from, to, initialUid, onBack }) {
  const { activeWorkspaceId } = useAuth();
  const [reps, setReps] = useState([]);
  const [uid, setUid] = useState(initialUid || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!activeWorkspaceId) return;
    (async () => {
      try {
        const { data } = await api.get(`/workspaces/${activeWorkspaceId}/sales-perf/leaderboard`, { params: { from_: from, to_: to } });
        setReps(data.items || []);
        if (!uid && (data.items || []).length) setUid(data.items[0].user_id);
      } catch (e) { toast.error(apiErrorMessage(e, "Rep list failed")); }
    })();
  }, [activeWorkspaceId, from, to, uid]);

  useEffect(() => {
    if (!activeWorkspaceId || !uid) return;
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/workspaces/${activeWorkspaceId}/sales-perf/users/${uid}`, { params: { from_: from, to_: to } });
        if (!cancel) setData(data);
      } catch (e) { if (!cancel) toast.error(apiErrorMessage(e, "Rep detail failed")); }
      finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [activeWorkspaceId, uid, from, to]);

  const maxDaily = useMemo(() => {
    if (!data?.series) return 1;
    return Math.max(1, ...data.series.map(d => d.calls + d.whatsapp + d.email_sent + d.leads_won));
  }, [data]);

  return (
    <div className="space-y-3" data-testid="sales-perf-per-rep">
      <div className="flex items-center gap-2">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Rep</div>
        <Select value={uid} onValueChange={setUid}>
          <SelectTrigger className="h-8 rounded-none w-72 text-xs" data-testid="perrep-select"><SelectValue placeholder="Pick a rep…" /></SelectTrigger>
          <SelectContent>
            {reps.map(r => <SelectItem key={r.user_id} value={r.user_id}>{r.user_name}{r.user_email ? ` · ${r.user_email}` : ""}</SelectItem>)}
          </SelectContent>
        </Select>
        {onBack && <Button variant="ghost" size="sm" className="rounded-none h-8" onClick={onBack}>← Leaderboard</Button>}
      </div>
      {loading || !data ? <div className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading rep data…</div> : (
        <>
          <div className="border p-4 bg-gradient-to-br from-indigo-50 to-transparent">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Overall score</div>
            <div className="text-4xl font-bold tabular-nums flex items-baseline gap-2" data-testid="perrep-score">
              {data.score} <span className="text-xs text-muted-foreground">{data.user.name}</span>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            <Kpi label="Prospects owned" value={data.totals.prospecting.owned} icon={Target} testid="perrep-prospects-owned" />
            <Kpi label="Prospects qualified" value={data.totals.prospecting.qualified} icon={Target} testid="perrep-prospects-qualified" tone="text-emerald-600" />
            <Kpi label="Calls · Answered" value={`${data.totals.calls.total} · ${data.totals.calls.answered}`} icon={PhoneCall} testid="perrep-calls" />
            <Kpi label="Talk time" value={fmtTalk(data.totals.calls.talk_time_sec)} icon={PhoneCall} testid="perrep-talk" />
            <Kpi label="Leads won" value={data.totals.leads.won} icon={Trophy} testid="perrep-leads-won" tone="text-emerald-700" />
            <Kpi label="Leads new" value={data.totals.leads.created} icon={Target} testid="perrep-leads-created" />
            <Kpi label="Leads lost" value={data.totals.leads.lost} icon={Target} testid="perrep-leads-lost" tone="text-rose-600" />
            <Kpi label="WhatsApp sent" value={data.totals.whatsapp.messages_sent} icon={MessageCircle} testid="perrep-wa-sent" />
            <Kpi label="WA unique recipients" value={data.totals.whatsapp.unique_recipients} icon={MessageCircle} testid="perrep-wa-recipients" />
            <Kpi label="Email sent" value={data.totals.email.sent} icon={Mail} testid="perrep-email-sent" />
          </div>
          <div className="border p-3" data-testid="perrep-daily-strip">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Daily activity</div>
            <div className="flex items-end gap-0.5 h-32">
              {data.series.map((d) => {
                const total = d.calls + d.whatsapp + d.email_sent + d.leads_won;
                const h = Math.max(2, Math.round((total / maxDaily) * 120));
                return (
                  <div key={d.date} className="flex-1 min-w-[6px] flex flex-col-reverse" title={`${d.date} · calls:${d.calls} wa:${d.whatsapp} email:${d.email_sent} won:${d.leads_won}`}>
                    <div className="bg-emerald-500" style={{ height: `${(d.leads_won / (total || 1)) * h}px` }} />
                    <div className="bg-amber-400" style={{ height: `${(d.email_sent / (total || 1)) * h}px` }} />
                    <div className="bg-sky-400" style={{ height: `${(d.whatsapp / (total || 1)) * h}px` }} />
                    <div className="bg-indigo-500" style={{ height: `${(d.calls / (total || 1)) * h}px` }} />
                  </div>
                );
              })}
            </div>
            <div className="mt-2 flex items-center gap-3 text-[10px] text-muted-foreground flex-wrap">
              <span className="flex items-center gap-1"><span className="w-2 h-2 bg-indigo-500 inline-block" /> Calls</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 bg-sky-400 inline-block" /> WhatsApp</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 bg-amber-400 inline-block" /> Email</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 bg-emerald-500 inline-block" /> Leads won</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// R52 · Daily Sales Report auto-email settings — compact popover
// ─────────────────────────────────────────────────────────────────
function DailyReportAutoEmail({ wsId }) {
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [emails, setEmails] = useState("");
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    if (!wsId) return;
    try {
      const { data } = await api.get(
        `/workspaces/${wsId}/sales-perf/daily-report-settings`);
      setCfg(data);
      setEmails((data.recipients || []).join(", "));
    } catch { /* silent */ }
  }, [wsId]);

  useEffect(() => { refresh(); }, [refresh]);

  const save = async (enable) => {
    setSaving(true);
    try {
      const recipients = emails.split(/[,\n\s]+/).map(s => s.trim()).filter(Boolean);
      const { data } = await api.put(
        `/workspaces/${wsId}/sales-perf/daily-report-settings`,
        { enabled: enable, recipients, hour: cfg?.hour ?? 9 });
      setCfg(data);
      setEmails((data.recipients || []).join(", "));
      toast.success(enable ? "Daily email scheduled" : "Daily email disabled");
      if (enable) setOpen(false);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Save failed"));
    } finally { setSaving(false); }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        data-testid="daily-report-schedule-btn"
        className={`inline-flex items-center gap-1 h-8 px-3 border text-xs font-semibold ${
          cfg?.enabled ? "border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-700"
                       : "border-border bg-background hover:border-primary"}`}
        title={cfg?.enabled ? "Daily email ON" : "Schedule daily email"}
      >
        <Bell className="w-3.5 h-3.5" />
        {cfg?.enabled ? "Auto-email ON" : "Schedule"}
      </button>
      {open && (
        <div className="absolute right-0 top-10 z-30 w-80 bg-background border border-border p-3 shadow-lg space-y-2"
              data-testid="daily-report-schedule-popover">
          <div className="text-xs font-semibold uppercase tracking-widest text-foreground/70">
            Daily Sales Report · auto-email
          </div>
          <p className="text-[11px] text-muted-foreground">
            Sends yesterday&apos;s PDF to selected admins at ~9 AM workspace time.
          </p>
          <label className="block text-[11px] font-semibold">Recipient emails <span className="text-muted-foreground">(comma or newline separated)</span></label>
          <textarea
            className="w-full h-24 border border-border p-2 text-xs font-mono rounded-none"
            value={emails}
            onChange={(e) => setEmails(e.target.value)}
            placeholder="admin@example.com, sales-lead@example.com"
            data-testid="daily-report-recipients"
          />
          <div className="flex items-center gap-2 justify-end">
            {cfg?.enabled && (
              <button
                type="button"
                onClick={() => save(false)}
                disabled={saving}
                className="text-xs h-8 px-2 border border-border hover:bg-muted"
                data-testid="daily-report-disable"
              >Turn off</button>
            )}
            <button
              type="button"
              onClick={() => save(true)}
              disabled={saving}
              className="text-xs h-8 px-3 bg-primary text-primary-foreground font-semibold hover:bg-primary/90 disabled:opacity-50"
              data-testid="daily-report-enable"
            >{saving ? "Saving…" : (cfg?.enabled ? "Update" : "Enable")}</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Page shell ────────────────────────────────────────────────────
export default function SalesPerfDashboard() {
  const { activeWorkspaceId } = useAuth();
  const { from, to, setFrom, setTo } = useRange();
  const [tab, setTab] = useState("leaderboard");
  const [drillUid, setDrillUid] = useState(null);

  const onDrill = (uid) => { setDrillUid(uid); setTab("per-rep"); };
  const onBack = () => setTab("leaderboard");

  return (
    <>
      <PageHeader
        eyebrow="Sales Performance"
        title="Team + per-rep dashboard"
        description="Cross-channel activity across prospecting, calls, WhatsApp, email campaigns and lead conversion. Filter by date range and drill into any rep."
      />
      <div className="px-4 sm:px-6 md:px-10 py-6 space-y-4" data-testid="sales-perf-dashboard">
        <div className="flex flex-wrap items-end gap-2 border p-3 bg-neutral-50">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">From</div>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-8 rounded-none text-xs" data-testid="sales-perf-from" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">To</div>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-8 rounded-none text-xs" data-testid="sales-perf-to" />
          </div>
          <div className="ml-auto flex gap-1 items-center flex-wrap">
            <Button variant="outline" size="sm" className="rounded-none h-8 text-xs" onClick={() => { setFrom(isoDaysAgo(0)); setTo(isoDaysAgo(0)); }} data-testid="range-today">Today</Button>
            <Button variant="outline" size="sm" className="rounded-none h-8 text-xs" onClick={() => { setFrom(isoDaysAgo(6)); setTo(isoDaysAgo(0)); }} data-testid="range-7d">7 days</Button>
            <Button variant="outline" size="sm" className="rounded-none h-8 text-xs" onClick={() => { setFrom(isoDaysAgo(29)); setTo(isoDaysAgo(0)); }} data-testid="range-30d">30 days</Button>
            <Button variant="outline" size="sm" className="rounded-none h-8 text-xs" onClick={() => { setFrom(isoDaysAgo(89)); setTo(isoDaysAgo(0)); }} data-testid="range-90d">90 days</Button>
            {/* R52 · Daily Sales Report PDF export */}
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/v1/workspaces/${activeWorkspaceId}/sales-perf/daily-report.pdf?date=${from}`}
              target="_blank" rel="noopener noreferrer"
              data-testid="download-daily-report-pdf"
              className="inline-flex items-center gap-1 h-8 px-3 border border-primary bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90"
              title={`Download Daily Sales Report for ${from}`}
            >
              <TrendingUp className="w-3.5 h-3.5" /> Daily report PDF
            </a>
            {/* R52 · Configure daily auto-email */}
            <DailyReportAutoEmail wsId={activeWorkspaceId} />
          </div>
        </div>

        <TeamSummary from={from} to={to} />

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="rounded-none">
            <TabsTrigger value="leaderboard" className="rounded-none" data-testid="sales-perf-tab-leaderboard">Team leaderboard</TabsTrigger>
            <TabsTrigger value="per-rep" className="rounded-none" data-testid="sales-perf-tab-per-rep">Per-rep detail</TabsTrigger>
          </TabsList>
          <TabsContent value="leaderboard" className="pt-4"><Leaderboard from={from} to={to} onDrill={onDrill} /></TabsContent>
          <TabsContent value="per-rep" className="pt-4"><PerRep from={from} to={to} initialUid={drillUid} onBack={onBack} /></TabsContent>
        </Tabs>
      </div>
    </>
  );
}
